import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Globe } from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';
import { cn } from 'shared/lib/utils';

interface LanguageSwitcherProps {
  variant?: 'header' | 'inline';
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'header' }) => {
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('click', handle);
      return () => document.removeEventListener('click', handle);
    }
  }, [open]);

  const current = availableLanguages.find((l) => l.code === language);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          'flex items-center gap-2 rounded-xl border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors',
          variant === 'header' ? 'px-3 py-2 text-sm' : 'px-4 py-2.5 text-sm font-medium shadow-sm',
        )}
      >
        <Globe size={20} className="text-haat-red-600" />
        <span className="font-medium">{current?.nativeLabel}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown size={18} className="text-gray-400" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-2 min-w-[200px] rounded-xl border border-gray-200 bg-white shadow-xl p-1.5 ltr:right-0 rtl:left-0"
            onClick={(e) => e.stopPropagation()}
          >
            {availableLanguages.map((lang) => {
              const isActive = lang.code === language;
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    setLanguage(lang.code);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive
                      ? 'bg-haat-red-50 text-haat-red-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  <span>{lang.nativeLabel}</span>
                  {isActive && <Check size={18} className="text-haat-red-600" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
